import React, { useId } from 'react';

import { KitPattern } from 'modules/teams/domain/enums/kit-pattern';

// ── PatternOverlay (SVG helper) ────────────────────────────────────────────

interface PatternOverlayProps {
  pattern: KitPattern;
  color2: string;
  width: number;
  height: number;
  rx?: number;
}

export const PatternOverlay: React.FC<PatternOverlayProps> = ({ pattern, color2, width, height, rx = 0 }) => {
  // Идентификатор клипа обязан быть уникальным на документ и стабильным между
  // рендерами: со `Math.random()` каждая перерисовка плодила новый `clipPath`.
  const uniqueId = useId().replace(/:/g, '');
  const clipId = `clip-${pattern}-${uniqueId}`;

  const renderShape = () => {
    switch (pattern) {
      case KitPattern.STRIPES_VERTICAL:
        return (
          <>
            <rect x={width * 0.33} y="0" width={width * 0.34} height={height} fill={color2} />
          </>
        );
      case KitPattern.STRIPES_HORIZONTAL:
        return <rect x="0" y={height * 0.33} width={width} height={height * 0.34} fill={color2} />;
      case KitPattern.HALVES:
        return <rect x={width / 2} y="0" width={width / 2} height={height} fill={color2} />;
      case KitPattern.QUARTERS:
        return (
          <>
            <rect x="0" y="0" width={width / 2} height={height / 2} fill={color2} />
            <rect x={width / 2} y={height / 2} width={width / 2} height={height / 2} fill={color2} />
          </>
        );
      case KitPattern.SASH:
        return <polygon points={`${width * 0.3},0 ${width},0 ${width * 0.7},${height} 0,${height}`} fill={color2} />;
      case KitPattern.CHECKERED: {
        const cw = width / 3;
        const ch = height / 3;
        return (
          <>
            {[0, 1, 2].flatMap((row) =>
              [0, 1, 2].map((col) =>
                (row + col) % 2 === 1 ? (
                  <rect key={`${row}-${col}`} x={col * cw} y={row * ch} width={cw} height={ch} fill={color2} />
                ) : null,
              ),
            )}
          </>
        );
      }
      case KitPattern.GRADIENT:
        return (
          <>
            <defs>
              <linearGradient id={`grad-${clipId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="transparent" />
                <stop offset="100%" stopColor={color2} />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width={width} height={height} fill={`url(#grad-${clipId})`} />
          </>
        );
      case KitPattern.PINSTRIPES:
        return (
          <>
            {[1, 3, 5].map((x) => (
              <rect key={x} x={(x / 6) * width - 0.5} y="0" width="1" height={height} fill={color2} opacity="0.7" />
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={width} height={height} rx={rx} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>{renderShape()}</g>
    </>
  );
};

// ── GarmentPattern ─────────────────────────────────────────────────────────

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Generic pattern fill that draws within an arbitrary bounding box (clipped to a garment shape by the caller).
export const GarmentPattern: React.FC<{ pattern: KitPattern; color2: string; box: Box; id: string }> = ({
  pattern,
  color2,
  box,
  id,
}) => {
  const { x, y, w, h } = box;
  switch (pattern) {
    case KitPattern.STRIPES_VERTICAL: {
      const n = 7;
      const sw = w / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) =>
            i % 2 === 1 ? <rect key={i} x={x + i * sw} y={y} width={sw} height={h} fill={color2} /> : null,
          )}
        </>
      );
    }
    case KitPattern.STRIPES_HORIZONTAL: {
      const n = 7;
      const sh = h / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) =>
            i % 2 === 1 ? <rect key={i} x={x} y={y + i * sh} width={w} height={sh} fill={color2} /> : null,
          )}
        </>
      );
    }
    case KitPattern.HALVES:
      return <rect x={x + w / 2} y={y} width={w / 2} height={h} fill={color2} />;
    case KitPattern.QUARTERS:
      return (
        <>
          <rect x={x} y={y} width={w / 2} height={h / 2} fill={color2} />
          <rect x={x + w / 2} y={y + h / 2} width={w / 2} height={h / 2} fill={color2} />
        </>
      );
    case KitPattern.SASH:
      return (
        <polygon points={`${x + w * 0.18},${y} ${x + w * 0.5},${y} ${x + w * 0.82},${y + h} ${x + w * 0.5},${y + h}`} fill={color2} />
      );
    case KitPattern.CHECKERED: {
      const cols = 4;
      const rows = 5;
      const cw = w / cols;
      const ch = h / rows;
      return (
        <>
          {Array.from({ length: rows }).flatMap((_, r) =>
            Array.from({ length: cols }).map((__, c) =>
              (r + c) % 2 === 1 ? <rect key={`${r}-${c}`} x={x + c * cw} y={y + r * ch} width={cw} height={ch} fill={color2} /> : null,
            ),
          )}
        </>
      );
    }
    case KitPattern.GRADIENT:
      return (
        <>
          <defs>
            <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="100%" stopColor={color2} stopOpacity="0.85" />
            </linearGradient>
          </defs>
          <rect x={x} y={y} width={w} height={h} fill={`url(#${id})`} />
        </>
      );
    case KitPattern.PINSTRIPES: {
      const n = 9;
      const gap = w / n;
      return (
        <>
          {Array.from({ length: n }).map((_, i) => (
            <rect key={i} x={x + i * gap + gap / 2} y={y} width={1.4} height={h} fill={color2} opacity={0.55} />
          ))}
        </>
      );
    }
    default:
      return null;
  }
};
